import { useMemo, useRef } from 'react';
import { Queue } from 'src/utils/Queue';
import {
  noteTakingEnd,
  noteTakingStart,
  notification,
  textChannelNewMessage,
  voiceInviteToChannel,
  voiceJoin,
  voiceJoinLeave,
  voiceMute,
  voiceMuteDeaf,
  voiceScreenShareEnd,
  voiceScreenShareStart,
} from './sounds';
import { AudioType } from './types';

type AudioProps = {
  audio: HTMLAudioElement;
  preload: boolean;
  loop?: boolean;
};

const sounds: Record<AudioType, AudioProps> = {
  [AudioType.CHANNEL_NEW_MESSAGE]: {
    audio: new Audio(textChannelNewMessage),
    preload: true,
  },
  [AudioType.CHANNEL_VOICE_ENTERING]: {
    audio: new Audio(voiceJoin),
    preload: true,
  },
  [AudioType.CHANNEL_VOICE_LEAVING]: {
    audio: new Audio(voiceJoinLeave),
    preload: true,
  },
  [AudioType.CHANNEL_VOICE_SCREEN_SHARING_START]: {
    audio: new Audio(voiceScreenShareStart),
    preload: true,
  },
  [AudioType.CHANNEL_VOICE_SCREEN_SHARING_END]: {
    audio: new Audio(voiceScreenShareEnd),
    preload: true,
  },
  [AudioType.CHANNEL_VOICE_INVITE]: {
    audio: new Audio(voiceInviteToChannel),
    preload: true,
    loop: true,
  },
  [AudioType.CHANNEL_VOICE_MUTE]: {
    audio: new Audio(voiceMute),
    preload: true,
  },
  [AudioType.CHANNEL_VOICE_DEAF]: {
    audio: new Audio(voiceMuteDeaf),
    preload: true,
  },
  [AudioType.NOTIFICATION]: {
    audio: new Audio(notification),
    preload: true,
  },
  [AudioType.NOTE_TAKING_START]: {
    audio: new Audio(noteTakingStart),
    preload: true,
  },
  [AudioType.NOTE_TAKING_END]: {
    audio: new Audio(noteTakingEnd),
    preload: true,
  },
};

const queue = new Queue<AudioType>();

const playAgainInterval = 3000;
const lastPlayed: Partial<Record<AudioType, number>> = {};

export function useAudio() {
  const playedSoundTypes = useRef<AudioType[]>([]);
  return useMemo(() => {
    const audioEnded = (type: AudioType) => {
      lastPlayed[type] = Date.now();
      queue.ack();

      const sound = sounds[type] || sounds[AudioType.NOTIFICATION];

      sound.audio.removeEventListener('ended', () => {
        audioEnded(type);
      });

      if (!queue.isEmpty()) {
        consume();
      }
    };

    const consume = () => {
      if (queue.isRunning()) {
        return;
      }

      const type = queue.dequeue();
      if (!type) return;

      // if the audio was played recently, don't play it again
      if (
        lastPlayed[type] &&
        lastPlayed[type] + playAgainInterval > Date.now()
      ) {
        queue.ack();
        return;
      }

      const sound = sounds[type] || sounds[AudioType.NOTIFICATION];

      if (!playedSoundTypes?.current?.includes(type)) {
        sound.audio.addEventListener('ended', () => {
          audioEnded(type);
        });
        playedSoundTypes.current.push(type);
      }

      sound.audio.loop = !!sound.loop;
      sound.audio.preload = sound.preload ? 'auto' : 'none';

      sound.audio.play();
    };

    return {
      play: (
        type: AudioType,
        { skipLastPlayed = false }: { skipLastPlayed?: boolean } = {},
      ) => {
        if (skipLastPlayed) {
          delete lastPlayed[type];
        }
        queue.enqueue(type);
        consume();
      },
      stop: (type: AudioType) => {
        const sound = sounds[type] || sounds[AudioType.NOTIFICATION];
        sound.audio.pause();
        audioEnded(type);
      },
    };
  }, []);
}
